import {
  getLocalizedFoodName,
  getResolvedFoodType,
  isCatalogFood,
  BEDCA_FOOD_CATALOG,
} from "./foodCatalog";
import {
  CATALOG_FOOD_SOURCE_EFORT,
  CATALOG_FOOD_SOURCE_OPEN_SOURCE,
  normalizeCatalogFoodSource,
  normalizeFoodSearchCode,
  normalizeFoodSearchText,
  shouldRunFuzzyFoodSearch,
  tokenizeFoodSearchText,
} from "./supabaseFoodCatalogShared";

export const FOOD_SEARCH_SURFACE_LIBRARY = "library";
export const FOOD_SEARCH_SURFACE_PICKER = "picker";
export const FOOD_SEARCH_DEBOUNCE_MS = 240;
export const FOOD_SEARCH_DEFAULT_PAGE_SIZE = 40;
export const FOOD_SEARCH_PICKER_PAGE_SIZE = 8;
export const FOOD_SEARCH_STRONG_LITERAL_MAX_TIER = 2;
export const FOOD_SEARCH_FUZZY_TIER = 5;

const FOOD_SEARCH_SKIP_FUZZY_AFTER_STRONG_COUNT = 5;
const FOOD_SEARCH_FUZZY_LIMIT_WITH_STRONG_LITERALS = 2;
const FOOD_SEARCH_FUZZY_LIMIT_DEFAULT = 5;

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getFiniteNumber(value, fallback = 0) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function compareNumbersDescending(leftValue, rightValue) {
  if (rightValue === leftValue) {
    return 0;
  }

  return rightValue - leftValue;
}

function compareStrings(leftValue, rightValue) {
  return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
    sensitivity: "base",
  });
}

function getFoodSourcePriority(food) {
  return isCatalogFood(food) ? 0 : 1;
}

function getFoodTypoSimilarity(food) {
  return getFiniteNumber(food?._typoSimilarity ?? food?.typo_similarity, 0);
}

function getFoodCatalogCode(food) {
  if (hasText(food?.code)) {
    return food.code;
  }

  return "";
}

function getCatalogSourceSearchText(food) {
  if (!isCatalogFood(food)) {
    return "";
  }

  const source = normalizeCatalogFoodSource(food?.source, {
    defaultSource: CATALOG_FOOD_SOURCE_EFORT,
  });

  if (source === CATALOG_FOOD_SOURCE_OPEN_SOURCE) {
    return "open source";
  }

  return "efort";
}

export function shouldFetchCatalogFoods({
  query = "",
  surface = FOOD_SEARCH_SURFACE_LIBRARY,
  filters = buildCanonicalFoodFilters(),
  coachFoods = [],
  pageSize = null,
  sortField = null,
} = {}) {
  const resolvedFilters = buildCanonicalFoodFilters(filters);
  if (resolvedFilters.source === "coach") {
    return false;
  }

  if (surface !== FOOD_SEARCH_SURFACE_PICKER) {
    return true;
  }

  if (resolvedFilters.source === "catalog") {
    return true;
  }

  if (!normalizeFoodSearchText(query) && hasText(sortField)) {
    return true;
  }

  const defaultPageSize =
    surface === FOOD_SEARCH_SURFACE_PICKER
      ? FOOD_SEARCH_PICKER_PAGE_SIZE
      : FOOD_SEARCH_DEFAULT_PAGE_SIZE;
  const resolvedPageSize = Math.max(1, Number(pageSize) || defaultPageSize);
  const shouldUseStrongMatchGate =
    normalizeFoodSearchText(query).length > 0 && shouldRunFuzzyFoodSearch(query);

  return getEligibleCoachFoodMatchCount({
    coachFoods,
    query,
    filters: resolvedFilters,
    strongOnly: shouldUseStrongMatchGate,
  }) < resolvedPageSize;
}

function getFoodSearchFields(food) {
  const localizedName = getLocalizedFoodName(food, "en");
  const normalizedName = normalizeFoodSearchText(food?.name ?? localizedName);
  const normalizedNameEn = normalizeFoodSearchText(food?.name_en ?? food?.name ?? localizedName);
  const normalizedNameEs = normalizeFoodSearchText(food?.name_es ?? food?.name ?? localizedName);
  const normalizedBrand = normalizeFoodSearchText(food?.brand);
  const normalizedSearchText = normalizeFoodSearchText([
    food?.name,
    food?.name_en,
    food?.name_es,
    localizedName,
    food?.brand,
    getCatalogSourceSearchText(food),
  ]
    .filter(hasText)
    .join(" "));

  return {
    code: normalizeFoodSearchCode(getFoodCatalogCode(food)),
    name: normalizedName,
    nameEn: normalizedNameEn,
    nameEs: normalizedNameEs,
    brand: normalizedBrand,
    text: normalizedSearchText,
  };
}

function getFoodWordList(food) {
  return getFoodSearchFields(food).text
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean);
}

function getPrefixPosition(food, query) {
  const fields = getFoodSearchFields(food);
  const searchableValues = [
    fields.name,
    fields.nameEn,
    fields.nameEs,
    fields.brand,
    fields.text,
  ].filter(Boolean);

  let minPosition = Number.MAX_SAFE_INTEGER;
  searchableValues.forEach((value) => {
    const directPosition = value.indexOf(query);
    const wordPosition = value.indexOf(` ${query}`);
    if (directPosition >= 0) {
      minPosition = Math.min(minPosition, directPosition);
    }
    if (wordPosition >= 0) {
      minPosition = Math.min(minPosition, wordPosition + 1);
    }
  });

  return Number.isFinite(minPosition) && minPosition !== Number.MAX_SAFE_INTEGER
    ? minPosition
    : Number.MAX_SAFE_INTEGER;
}

function getClosestMatchLength(food, query) {
  const fields = getFoodSearchFields(food);
  const searchableValues = [
    fields.name,
    fields.nameEn,
    fields.nameEs,
    fields.brand,
    fields.text,
  ].filter(Boolean);
  let minLength = Number.MAX_SAFE_INTEGER;

  searchableValues.forEach((value) => {
    if (value.includes(query)) {
      minLength = Math.min(minLength, value.length);
    }
  });

  return Number.isFinite(minLength) ? minLength : Number.MAX_SAFE_INTEGER;
}

function hasWordPrefix(value, query) {
  return value.startsWith(query) || value.includes(` ${query}`);
}

function hasAllTokenPrefixes(food, tokens) {
  if (tokens.length === 0) {
    return false;
  }

  const words = getFoodWordList(food);
  if (words.length === 0) {
    return false;
  }

  return tokens.every((token) =>
    words.some((word) => word.startsWith(token)),
  );
}

function hasAllTokens(food, tokens) {
  if (tokens.length === 0) {
    return false;
  }

  const searchableText = getFoodSearchFields(food).text;
  return tokens.every((token) => searchableText.includes(token));
}

function getMatchedTokenCount(food, tokens) {
  if (tokens.length === 0) {
    return 0;
  }

  const searchableText = getFoodSearchFields(food).text;
  return tokens.reduce(
    (count, token) => count + (searchableText.includes(token) ? 1 : 0),
    0,
  );
}

function getUniqueFuzzySearchWords(food) {
  const fields = getFoodSearchFields(food);
  return Array.from(
    new Set(
      [fields.name, fields.nameEn, fields.nameEs, fields.brand]
        .filter(Boolean)
        .flatMap((value) => value.split(" "))
        .map((word) => word.trim())
        .filter(Boolean),
    ),
  );
}

function getMaxAllowedTypoDistance(leftValue, rightValue) {
  const maxLength = Math.max(
    String(leftValue ?? "").length,
    String(rightValue ?? "").length,
  );
  return maxLength >= 8 ? 2 : 1;
}

function getDamerauLevenshteinDistance(leftValue, rightValue, maxDistance = 2) {
  const left = String(leftValue ?? "");
  const right = String(rightValue ?? "");

  if (left === right) {
    return 0;
  }

  if (Math.abs(left.length - right.length) > maxDistance) {
    return maxDistance + 1;
  }

  const matrix = Array.from({ length: left.length + 1 }, () =>
    new Array(right.length + 1).fill(0),
  );

  for (let rowIndex = 0; rowIndex <= left.length; rowIndex += 1) {
    matrix[rowIndex][0] = rowIndex;
  }

  for (let columnIndex = 0; columnIndex <= right.length; columnIndex += 1) {
    matrix[0][columnIndex] = columnIndex;
  }

  for (let rowIndex = 1; rowIndex <= left.length; rowIndex += 1) {
    let rowMinDistance = Number.MAX_SAFE_INTEGER;

    for (let columnIndex = 1; columnIndex <= right.length; columnIndex += 1) {
      const substitutionCost = left[rowIndex - 1] === right[columnIndex - 1] ? 0 : 1;
      let nextDistance = Math.min(
        matrix[rowIndex - 1][columnIndex] + 1,
        matrix[rowIndex][columnIndex - 1] + 1,
        matrix[rowIndex - 1][columnIndex - 1] + substitutionCost,
      );

      if (
        rowIndex > 1 &&
        columnIndex > 1 &&
        left[rowIndex - 1] === right[columnIndex - 2] &&
        left[rowIndex - 2] === right[columnIndex - 1]
      ) {
        nextDistance = Math.min(
          nextDistance,
          matrix[rowIndex - 2][columnIndex - 2] + 1,
        );
      }

      matrix[rowIndex][columnIndex] = nextDistance;
      rowMinDistance = Math.min(rowMinDistance, nextDistance);
    }

    if (rowMinDistance > maxDistance) {
      return maxDistance + 1;
    }
  }

  return matrix[left.length][right.length];
}

function getBestFuzzyTokenCandidate(token, words) {
  const resolvedWords = Array.isArray(words) ? words : [];
  let bestCandidate = null;

  resolvedWords.forEach((word) => {
    const maxDistance = getMaxAllowedTypoDistance(token, word);
    const distance = getDamerauLevenshteinDistance(token, word, maxDistance);

    if (distance > maxDistance) {
      return;
    }

    const similarity =
      1 - distance / Math.max(token.length, word.length, 1);

    if (
      bestCandidate == null ||
      distance < bestCandidate.distance ||
      (distance === bestCandidate.distance && similarity > bestCandidate.similarity) ||
      (distance === bestCandidate.distance &&
        similarity === bestCandidate.similarity &&
        word.length < bestCandidate.word.length)
    ) {
      bestCandidate = {
        word,
        distance,
        similarity,
      };
    }
  });

  return bestCandidate;
}

function getLocalFuzzyFoodSearchMatch(food, query) {
  if (!shouldRunFuzzyFoodSearch(query)) {
    return null;
  }

  const tokens = tokenizeFoodSearchText(query).filter((token) => token.length >= 2);
  const words = getUniqueFuzzySearchWords(food);

  if (tokens.length === 0 || words.length === 0) {
    return null;
  }

  let totalDistance = 0;
  let similarityTotal = 0;
  let matchedTokenCount = 0;
  let usedTypoCorrection = false;
  let minWordLength = Number.MAX_SAFE_INTEGER;

  for (const token of tokens) {
    const bestCandidate = getBestFuzzyTokenCandidate(token, words);

    if (!bestCandidate) {
      return null;
    }

    matchedTokenCount += 1;
    totalDistance += bestCandidate.distance;
    similarityTotal += bestCandidate.similarity;
    minWordLength = Math.min(minWordLength, bestCandidate.word.length);

    if (bestCandidate.distance > 0) {
      usedTypoCorrection = true;
    }
  }

  if (!usedTypoCorrection) {
    return null;
  }

  const averageSimilarity = similarityTotal / Math.max(tokens.length, 1);
  if (averageSimilarity < 0.72) {
    return null;
  }

  return {
    tier: FOOD_SEARCH_FUZZY_TIER,
    matchKind: "fuzzy",
    exactFieldPriority: Number.MAX_SAFE_INTEGER,
    matchedTokenCount,
    prefixPosition: Number.MAX_SAFE_INTEGER,
    matchLength: Number.isFinite(minWordLength) ? minWordLength : Number.MAX_SAFE_INTEGER,
    fuzzySimilarity: averageSimilarity,
    fuzzyDistance: totalDistance,
  };
}

function getFuzzyFoodSearchMatch(food, query) {
  if (!shouldRunFuzzyFoodSearch(query)) {
    return null;
  }

  const localMatch = getLocalFuzzyFoodSearchMatch(food, query);
  const remoteSimilarity = getFoodTypoSimilarity(food);

  if (remoteSimilarity <= 0 && !localMatch) {
    return null;
  }

  const tokens = tokenizeFoodSearchText(query).filter(Boolean);

  return {
    tier: FOOD_SEARCH_FUZZY_TIER,
    matchKind: "fuzzy",
    exactFieldPriority: Number.MAX_SAFE_INTEGER,
    matchedTokenCount: localMatch?.matchedTokenCount ?? tokens.length,
    prefixPosition: Number.MAX_SAFE_INTEGER,
    matchLength: localMatch?.matchLength ?? Number.MAX_SAFE_INTEGER,
    fuzzySimilarity: Math.max(remoteSimilarity, localMatch?.fuzzySimilarity ?? 0),
    fuzzyDistance: localMatch?.fuzzyDistance ?? Number.MAX_SAFE_INTEGER,
  };
}

function isStrongLiteralFoodMatch(match) {
  return (
    match?.matchKind !== "fuzzy" &&
    Number.isFinite(match?.tier) &&
    match.tier <= FOOD_SEARCH_STRONG_LITERAL_MAX_TIER
  );
}

function resolveFuzzyResultLimit(strongLiteralCount) {
  if (strongLiteralCount >= FOOD_SEARCH_SKIP_FUZZY_AFTER_STRONG_COUNT) {
    return 0;
  }

  if (strongLiteralCount > 0) {
    return FOOD_SEARCH_FUZZY_LIMIT_WITH_STRONG_LITERALS;
  }

  return FOOD_SEARCH_FUZZY_LIMIT_DEFAULT;
}

export function buildCanonicalFoodFilters({
  source = "all",
  type = "all",
} = {}) {
  return {
    source: source === "coach" || source === "catalog" ? source : "all",
    type: type === "food" || type === "supplement" ? type : "all",
  };
}

export function matchesCanonicalFoodFilters(food, filters = buildCanonicalFoodFilters()) {
  const sourceKind = isCatalogFood(food) ? "catalog" : "coach";
  const resolvedType = getResolvedFoodType(food);

  if (filters?.source === "coach" && sourceKind !== "coach") {
    return false;
  }

  if (filters?.source === "catalog" && sourceKind !== "catalog") {
    return false;
  }

  if (filters?.type === "food" && resolvedType !== "food") {
    return false;
  }

  if (filters?.type === "supplement" && resolvedType !== "supplement") {
    return false;
  }

  return true;
}

function getMatchingCoachFoods({
  coachFoods = [],
  query = "",
  filters = buildCanonicalFoodFilters(),
  strongOnly = false,
} = {}) {
  const normalizedQuery = normalizeFoodSearchText(query);

  return (Array.isArray(coachFoods) ? coachFoods : []).filter((food) => {
    if (!matchesCanonicalFoodFilters(food, filters)) {
      return false;
    }

    const match = !normalizedQuery
      ? {
          tier: Number.MAX_SAFE_INTEGER,
          matchKind: "literal",
        }
      : getCanonicalFoodSearchMatch(food, normalizedQuery);

    if (!match) {
      return false;
    }

    return strongOnly ? isStrongLiteralFoodMatch(match) : true;
  });
}

export function getEligibleCoachFoodMatchCount({
  coachFoods = [],
  query = "",
  filters = buildCanonicalFoodFilters(),
  strongOnly = false,
} = {}) {
  return getMatchingCoachFoods({
    coachFoods,
    query,
    filters: buildCanonicalFoodFilters(filters),
    strongOnly,
  }).length;
}

export function getCanonicalFoodSearchMatch(food, query, { allowFuzzy = true } = {}) {
  const normalizedQuery = normalizeFoodSearchText(query);
  if (!normalizedQuery) {
    return {
      tier: Number.MAX_SAFE_INTEGER,
      matchKind: "literal",
      exactFieldPriority: Number.MAX_SAFE_INTEGER,
      matchedTokenCount: 0,
      prefixPosition: Number.MAX_SAFE_INTEGER,
      matchLength: Number.MAX_SAFE_INTEGER,
      fuzzySimilarity: 0,
      fuzzyDistance: Number.MAX_SAFE_INTEGER,
    };
  }

  const tokens = tokenizeFoodSearchText(query);
  const fields = getFoodSearchFields(food);

  if (fields.code && fields.code === normalizeFoodSearchCode(query)) {
    return {
      tier: 0,
      matchKind: "literal",
      exactFieldPriority: 0,
      matchedTokenCount: tokens.length,
      prefixPosition: 0,
      matchLength: fields.code.length,
      fuzzySimilarity: 0,
      fuzzyDistance: Number.MAX_SAFE_INTEGER,
    };
  }

  const exactFieldIndex = [fields.name, fields.nameEn, fields.nameEs, fields.brand].findIndex(
    (value) => value === normalizedQuery,
  );
  if (exactFieldIndex >= 0) {
    return {
      tier: 1,
      matchKind: "literal",
      exactFieldPriority: exactFieldIndex,
      matchedTokenCount: tokens.length,
      prefixPosition: 0,
      matchLength: normalizedQuery.length,
      fuzzySimilarity: 0,
      fuzzyDistance: Number.MAX_SAFE_INTEGER,
    };
  }

  if (
    hasWordPrefix(fields.name, normalizedQuery) ||
    hasWordPrefix(fields.nameEn, normalizedQuery) ||
    hasWordPrefix(fields.nameEs, normalizedQuery) ||
    hasWordPrefix(fields.brand, normalizedQuery) ||
    hasAllTokenPrefixes(food, tokens)
  ) {
    return {
      tier: 2,
      matchKind: "literal",
      exactFieldPriority: Number.MAX_SAFE_INTEGER,
      matchedTokenCount: getMatchedTokenCount(food, tokens),
      prefixPosition: getPrefixPosition(food, normalizedQuery),
      matchLength: getClosestMatchLength(food, normalizedQuery),
      fuzzySimilarity: 0,
      fuzzyDistance: Number.MAX_SAFE_INTEGER,
    };
  }

  if (hasAllTokens(food, tokens)) {
    return {
      tier: 3,
      matchKind: "literal",
      exactFieldPriority: Number.MAX_SAFE_INTEGER,
      matchedTokenCount: getMatchedTokenCount(food, tokens),
      prefixPosition: getPrefixPosition(food, tokens[0] ?? normalizedQuery),
      matchLength: getClosestMatchLength(food, tokens[0] ?? normalizedQuery),
      fuzzySimilarity: 0,
      fuzzyDistance: Number.MAX_SAFE_INTEGER,
    };
  }

  if (
    fields.text.includes(normalizedQuery) ||
    fields.name.includes(normalizedQuery) ||
    fields.nameEn.includes(normalizedQuery) ||
    fields.nameEs.includes(normalizedQuery) ||
    fields.brand.includes(normalizedQuery) ||
    getMatchedTokenCount(food, tokens) > 0
  ) {
    return {
      tier: 4,
      matchKind: "literal",
      exactFieldPriority: Number.MAX_SAFE_INTEGER,
      matchedTokenCount: getMatchedTokenCount(food, tokens),
      prefixPosition: getPrefixPosition(food, tokens[0] ?? normalizedQuery),
      matchLength: getClosestMatchLength(food, tokens[0] ?? normalizedQuery),
      fuzzySimilarity: 0,
      fuzzyDistance: Number.MAX_SAFE_INTEGER,
    };
  }

  if (allowFuzzy !== false) {
    return getFuzzyFoodSearchMatch(food, normalizedQuery);
  }

  return null;
}

export function compareCanonicalFoodResults(
  leftFood,
  rightFood,
  {
    query = "",
    sortField = null,
    language = "en",
  } = {},
) {
  const normalizedQuery = normalizeFoodSearchText(query);
  const leftMatch = getCanonicalFoodSearchMatch(leftFood, normalizedQuery);
  const rightMatch = getCanonicalFoodSearchMatch(rightFood, normalizedQuery);

  if (normalizedQuery) {
    const tierComparison = compareNumbersDescending(
      -(leftMatch?.tier ?? Number.MAX_SAFE_INTEGER),
      -(rightMatch?.tier ?? Number.MAX_SAFE_INTEGER),
    );
    if (tierComparison !== 0) {
      return tierComparison;
    }

    const exactFieldComparison = compareNumbersDescending(
      -(leftMatch?.exactFieldPriority ?? Number.MAX_SAFE_INTEGER),
      -(rightMatch?.exactFieldPriority ?? Number.MAX_SAFE_INTEGER),
    );
    if (exactFieldComparison !== 0) {
      return exactFieldComparison;
    }

    const tokenComparison = compareNumbersDescending(
      leftMatch?.matchedTokenCount ?? 0,
      rightMatch?.matchedTokenCount ?? 0,
    );
    if (tokenComparison !== 0) {
      return tokenComparison;
    }

    const prefixComparison = compareNumbersDescending(
      -(leftMatch?.prefixPosition ?? Number.MAX_SAFE_INTEGER),
      -(rightMatch?.prefixPosition ?? Number.MAX_SAFE_INTEGER),
    );
    if (prefixComparison !== 0) {
      return prefixComparison;
    }

    const fuzzySimilarityComparison = compareNumbersDescending(
      leftMatch?.fuzzySimilarity ?? 0,
      rightMatch?.fuzzySimilarity ?? 0,
    );
    if (fuzzySimilarityComparison !== 0) {
      return fuzzySimilarityComparison;
    }

    const fuzzyDistanceComparison = compareNumbersDescending(
      -(leftMatch?.fuzzyDistance ?? Number.MAX_SAFE_INTEGER),
      -(rightMatch?.fuzzyDistance ?? Number.MAX_SAFE_INTEGER),
    );
    if (fuzzyDistanceComparison !== 0) {
      return fuzzyDistanceComparison;
    }

    const matchLengthComparison = compareNumbersDescending(
      -(leftMatch?.matchLength ?? Number.MAX_SAFE_INTEGER),
      -(rightMatch?.matchLength ?? Number.MAX_SAFE_INTEGER),
    );
    if (matchLengthComparison !== 0) {
      return matchLengthComparison;
    }
  }

  if (sortField) {
    const sortComparison = compareNumbersDescending(
      getFiniteNumber(leftFood?.[sortField]),
      getFiniteNumber(rightFood?.[sortField]),
    );
    if (sortComparison !== 0) {
      return sortComparison;
    }
  }

  if (normalizedQuery) {
    // Prefer coach foods only after text relevance and nutrient sorting are tied.
    const sourceComparison = compareNumbersDescending(
      getFoodSourcePriority(leftFood),
      getFoodSourcePriority(rightFood),
    );
    if (sourceComparison !== 0) {
      return sourceComparison;
    }
  }

  const nameComparison = compareStrings(
    getLocalizedFoodName(leftFood, language),
    getLocalizedFoodName(rightFood, language),
  );
  if (nameComparison !== 0) {
    return nameComparison;
  }

  return compareStrings(leftFood?.id, rightFood?.id);
}

export function buildCanonicalFoodSearchResults({
  coachFoods = [],
  catalogFoods = [],
  query = "",
  filters = buildCanonicalFoodFilters(),
  sortField = null,
  language = "en",
} = {}) {
  const normalizedQuery = normalizeFoodSearchText(query);
  const resolvedFilters = buildCanonicalFoodFilters(filters);

  if (!normalizedQuery) {
    const matchingCoachFoods = getMatchingCoachFoods({
      coachFoods,
      query: normalizedQuery,
      filters: resolvedFilters,
    });
    const coachCodes = new Set(
      matchingCoachFoods
        .map((food) => getFoodSearchFields(food).code)
        .filter(Boolean),
    );
    const matchingCatalogFoods = (Array.isArray(catalogFoods) ? catalogFoods : []).filter(
      (food) =>
        matchesCanonicalFoodFilters(food, resolvedFilters) &&
        !coachCodes.has(getFoodSearchFields(food).code),
    );
    const sortOptions = { query: normalizedQuery, sortField, language };

    if (!sortField) {
      return [
        ...matchingCoachFoods.sort((leftFood, rightFood) =>
          compareCanonicalFoodResults(leftFood, rightFood, sortOptions),
        ),
        ...matchingCatalogFoods.sort((leftFood, rightFood) =>
          compareCanonicalFoodResults(leftFood, rightFood, sortOptions),
        ),
      ];
    }

    return [...matchingCoachFoods, ...matchingCatalogFoods].sort(
      (leftFood, rightFood) =>
        compareCanonicalFoodResults(leftFood, rightFood, sortOptions),
    );
  }

  const coachCandidates = (Array.isArray(coachFoods) ? coachFoods : [])
    .filter((food) => matchesCanonicalFoodFilters(food, resolvedFilters))
    .map((food) => ({
      food,
      match: getCanonicalFoodSearchMatch(food, normalizedQuery),
    }))
    .filter((entry) => entry.match != null);

  const coachCodes = new Set(
    coachCandidates
      .map(({ food }) => getFoodSearchFields(food).code)
      .filter(Boolean),
  );
  const catalogCandidates = (Array.isArray(catalogFoods) ? catalogFoods : [])
    .filter((food) => matchesCanonicalFoodFilters(food, resolvedFilters))
    .map((food) => ({
      food,
      match: getCanonicalFoodSearchMatch(food, normalizedQuery),
    }))
    .filter(
      (entry) =>
        entry.match != null &&
        !coachCodes.has(getFoodSearchFields(entry.food).code),
    );

  const literalEntries = [...coachCandidates, ...catalogCandidates].filter(
    ({ match }) => match?.matchKind !== "fuzzy",
  );
  const fuzzyEntries = [...coachCandidates, ...catalogCandidates].filter(
    ({ match }) => match?.matchKind === "fuzzy",
  );
  const strongLiteralCount = literalEntries.filter(({ match }) =>
    isStrongLiteralFoodMatch(match),
  ).length;
  const fuzzyLimit = resolveFuzzyResultLimit(strongLiteralCount);
  const sortOptions = { query: normalizedQuery, sortField, language };
  const sortedLiteralFoods = literalEntries
    .map(({ food }) => food)
    .sort((leftFood, rightFood) =>
      compareCanonicalFoodResults(leftFood, rightFood, sortOptions),
    );

  if (fuzzyLimit === 0 || fuzzyEntries.length === 0) {
    return sortedLiteralFoods;
  }

  const sortedFuzzyFoods = fuzzyEntries
    .map(({ food }) => food)
    .sort((leftFood, rightFood) =>
      compareCanonicalFoodResults(leftFood, rightFood, sortOptions),
    );
  const selectedFuzzyFoods = [];

  for (const food of sortedFuzzyFoods) {
    if (selectedFuzzyFoods.length >= fuzzyLimit) {
      break;
    }

    selectedFuzzyFoods.push(food);
  }

  return [...sortedLiteralFoods, ...selectedFuzzyFoods];
}

export function paginateCanonicalFoodResults(foods, { cursor = 0, pageSize = 0 } = {}) {
  const resolvedFoods = Array.isArray(foods) ? foods : [];
  const safeCursor = Math.max(0, Number(cursor) || 0);
  const safePageSize = Math.max(0, Number(pageSize) || 0);

  if (safePageSize === 0) {
    return {
      foods: resolvedFoods,
      hasMore: false,
      nextCursor: null,
    };
  }

  const foodsPage = resolvedFoods.slice(safeCursor, safeCursor + safePageSize);
  const hasMore = safeCursor + safePageSize < resolvedFoods.length;

  return {
    foods: foodsPage,
    hasMore,
    nextCursor: hasMore ? safeCursor + safePageSize : null,
  };
}

export function getFallbackCatalogFoods() {
  return BEDCA_FOOD_CATALOG;
}
